import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { AuthAppContext } from "components/root/auth-app-provider";
import { useKeystone } from "data/ss-data-hooks";
import { UnauthorizedRequestError, isError } from "data/ss-error";
import { handleUnauthorized, logError } from "utils";
import { type KeystoneOfferEligibility } from "utils/keystone-api-mapping";

// path looks like "/my/property-services/:offerInternalName/:offerStep"
const OFFER_INTERNAL_NAME_PATH_INDEX = 3;

export const useEligibility = () => {
  const keystone = useKeystone();
  const navigate = useNavigate();
  const { policy } = useContext(AuthAppContext);
  const [eligibilityData, setEligibilityData] =
    useState<KeystoneOfferEligibility>();
  const [loading, setLoading] = useState(!policy?.insightPolicyId);

  const { pathname, state: locationState } = useLocation();
  const offerInternalName = pathname.split("/")[OFFER_INTERNAL_NAME_PATH_INDEX];

  useEffect(() => {
    if (!policy?.insightPolicyId) {
      return;
    }

    const handleRedirects = (data: KeystoneOfferEligibility) => {
      if (!data.isEligible) {
        // EXPIRED?
        navigate("/my/property-services/ineligible", { replace: true });
      } else if (data.offerStatusCode === "ENROLLED") {
        navigate(
          `/my/property-services/${data.offeringInternalName}/registered`,
          {
            replace: true,
          }
        );
      } else {
        return;
      }
    };

    const handleNotOk = async (res, endpoint) => {
      if (res instanceof UnauthorizedRequestError) {
        handleUnauthorized({ endpoint });
      } else {
        logError(`Creating Offer: ${res.status} ${res.statusText}`);
        navigate("/my/overview");
      }
    };

    // Fetch the eligibility data if needed
    if (
      offerInternalName === (eligibilityData?.offeringInternalName ?? "N/A") &&
      eligibilityData?.insightPolicyId === policy.insightPolicyId
    ) {
      handleRedirects(eligibilityData);
    } else {
      setLoading(true);
      (async () => {
        const data = await keystone.createOffer(
          policy.insightPolicyId,
          offerInternalName
        );
        if (!isError(data)) {
          handleRedirects(data);
          setEligibilityData(data);
        } else {
          await handleNotOk(
            data,
            `offer/${policy.insightPolicyId}/offering/${offerInternalName}`
          );
          return;
        }
        setLoading(false);
      })();
    }
  }, [
    keystone,
    policy.insightPolicyId,
    navigate,
    pathname,
    locationState,
    offerInternalName,
    eligibilityData,
  ]);

  return { eligibilityData, loading };
};
